import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Tag, Descriptions, Dropdown, Select, InputNumber
} from "antd";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, TeamOutlined,
    SearchOutlined, CheckCircleFilled, CloseCircleFilled,
    FilterOutlined, ClearOutlined, EyeOutlined, DeleteOutlined, MoreOutlined
} from "@ant-design/icons";
import api from "../../services/axiosInstance";
import "../../styles/fluidDesign.css";

const { Content } = Layout;

const CUSTOMER_TYPE_OPTIONS = [
    { value: "Individual", label: "Individual" },
    { value: "Company", label: "Company" },
    { value: "Partnership", label: "Partnership" },
    { value: "Other", label: "Other" },
];

const STATUS_OPTIONS = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
    { value: "Suspended", label: "Suspended" },
    { value: "Pending", label: "Pending" },
];

const ManageCustomer = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [viewItem, setViewItem] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    // Dropdown data
    const [areas, setAreas] = useState([]);
    const [countries, setCountries] = useState([]);

    // Filters
    const [search, setSearch] = useState('');
    const [filterArea, setFilterArea] = useState(null);
    const [filterStatus, setFilterStatus] = useState('');
    const [applied, setApplied] = useState({});

    const fetchDropdowns = useCallback(async () => {
        try {
            const [areasRes, countriesRes] = await Promise.all([
                api.get('/customers/areas.php'),
                api.get('/customers/countries.php'),
            ]);
            setAreas(areasRes.data.dataset || []);
            setCountries(countriesRes.data.dataset || []);
        } catch {
            // Silently fail — dropdowns will be empty
        }
    }, []);

    useEffect(() => { fetchDropdowns(); }, [fetchDropdowns]);

    // Live search: debounce and auto-trigger when search text changes
    const searchTimerRef = useRef(null);
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

        // If search is cleared, immediately reset to show all results
        if (search.trim() === '') {
            setApplied((prev) => ({ ...prev, search: '' }));
            fetchCustomers(1, pagination.pageSize, { search: '', area_id: filterArea || '', cus_status: filterStatus || '' });
            return;
        }

        // Debounce: only search after user stops typing for 400ms, and only if >=2 chars
        if (search.trim().length >= 2) {
            searchTimerRef.current = setTimeout(() => {
                const filters = { search: search.trim(), area_id: filterArea || '', cus_status: filterStatus || '' };
                setApplied(filters);
                fetchCustomers(1, pagination.pageSize, filters);
            }, 400);
        }

        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, [search]);

    const fetchCustomers = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
        try {
            setLoading(true);
            const res = await api.get(`/customers/list.php`, {
                params: {
                    page,
                    limit: pageSize,
                    search: filters.search || '',
                    area_id: filters.area_id || '',
                    cus_status: filters.cus_status || '',
                },
            });
            setCustomers(res.data.dataset || []);
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch customers.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchCustomers(1, 10, {}); }, [fetchCustomers]);
    const handleTableChange = (pg) => fetchCustomers(pg.current, pg.pageSize, applied);

    const handleFilter = () => {
        const filters = {
            search: search.trim(),
            area_id: filterArea || '',
            cus_status: filterStatus || '',
        };
        setApplied(filters);
        fetchCustomers(1, pagination.pageSize, filters);
    };

    const handleClear = () => {
        setSearch('');
        setFilterArea(null);
        setFilterStatus('');
        setApplied({});
        fetchCustomers(1, pagination.pageSize, {});
    };

    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/customers/add.php', {
                cus_type: values.cus_type || '',
                cus_name: values.cus_name,
                cus_reg_no: values.cus_reg_no || '',
                cus_area_id: values.cus_area_id || 0,
                cus_bill_address: values.cus_bill_address || '',
                cus_bill_country: values.cus_bill_country || '',
                cus_ship_address: values.cus_ship_address || '',
                cus_ship_country: values.cus_ship_country || '',
                cus_contact: values.cus_contact || '',
                cus_email: values.cus_email || '',
                cus_outstanding: values.cus_outstanding || 0,
                cus_status: values.cus_status || '',
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Customer added successfully.");
                setIsAddModalVisible(false);
                fetchCustomers(pagination.current, pagination.pageSize, applied);
                addForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add customer.");
        } finally { setLoading(false); }
    };

    const openEdit = (record) => {
        setCurrentItem(record);
        editForm.setFieldsValue({
            cus_ref: record.cus_ref || '',
            cus_type: record.cus_type || '',
            cus_name: record.cus_name,
            cus_reg_no: record.cus_reg_no || '',
            cus_area_id: record.cus_area_id ? Number(record.cus_area_id) : undefined,
            cus_bill_address: record.cus_bill_address || '',
            cus_bill_country: record.cus_bill_country || undefined,
            cus_ship_address: record.cus_ship_address || '',
            cus_ship_country: record.cus_ship_country || undefined,
            cus_contact: record.cus_contact || '',
            cus_email: record.cus_email || '',
            cus_outstanding: record.cus_outstanding ? Number(record.cus_outstanding) : 0,
            cus_status: record.cus_status || '',
        });
        setIsEditModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/customers/edit.php', {
                cus_id: currentItem.cus_id,
                cus_ref: values.cus_ref || '',
                cus_type: values.cus_type || '',
                cus_name: values.cus_name,
                cus_reg_no: values.cus_reg_no || '',
                cus_area_id: values.cus_area_id || 0,
                cus_bill_address: values.cus_bill_address || '',
                cus_bill_country: values.cus_bill_country || '',
                cus_ship_address: values.cus_ship_address || '',
                cus_ship_country: values.cus_ship_country || '',
                cus_contact: values.cus_contact || '',
                cus_email: values.cus_email || '',
                cus_outstanding: values.cus_outstanding || 0,
                cus_status: values.cus_status || '',
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Customer updated successfully.");
                setIsEditModalVisible(false);
                fetchCustomers(pagination.current, pagination.pageSize, applied);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update customer.");
        } finally { setLoading(false); }
    };

    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/customers/toggle.php', {
                cus_id: record.cus_id,
                cus_is_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchCustomers(pagination.current, pagination.pageSize, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    const handleDelete = async (record) => {
        try {
            setLoading(true);
            const res = await api.post('/customers/delete.php', {
                cus_id: record.cus_id,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Customer deleted successfully.");
                fetchCustomers(pagination.current, pagination.pageSize, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to delete customer.");
        } finally { setLoading(false); }
    };

    const openView = async (record) => {
        setViewItem(record);
        setIsViewModalVisible(true);
        try {
            const res = await api.get(`/customers/detail.php?cus_id=${record.cus_id}`);
            if (res.data.status === "success") setViewItem(res.data);
        } catch {
            // Keep the row data if the detail call fails
        }
    };

    const columns = [{
        title: 'ID', dataIndex: 'cus_id', key: 'cus_id', width: 60,
    },
    {
        title: 'Ref', dataIndex: 'cus_ref', key: 'cus_ref', width: 130,
        render: (val) => val || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
        title: 'Name', dataIndex: 'cus_name', key: 'cus_name', width: 200,
        sorter: (a, b) => (a.cus_name || '').localeCompare(b.cus_name || ''),
    },
    {
        title: 'Type', dataIndex: 'cus_type', key: 'cus_type', width: 110,
        render: (val) => val ? <Tag color="blue">{val}</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
        title: 'Area', dataIndex: 'area_name', key: 'area_name', width: 150,
        render: (val) => val || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
        title: 'Contact', dataIndex: 'cus_contact', key: 'cus_contact', width: 120,
        render: (val) => val || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
        title: 'Email', dataIndex: 'cus_email', key: 'cus_email', width: 170, ellipsis: true,
        render: (val) => val || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
        title: 'Outstanding', dataIndex: 'cus_outstanding', key: 'cus_outstanding', width: 120, align: 'right',
        render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '0.00',
    },
    {
        title: 'Status', dataIndex: 'cus_status', key: 'cus_status', width: 110,
        render: (val) => {
            if (!val) return <Typography.Text type="secondary">—</Typography.Text>;
            const colorMap = { Active: 'green', Inactive: 'default', Suspended: 'red', Pending: 'orange' };
            return <Tag color={colorMap[val] || 'default'}>{val}</Tag>;
        },
    },
    {
        title: 'Active', dataIndex: 'cus_is_active', key: 'cus_is_active', align: 'center', width: 70,
        render: (val) => val == 1
            ? <Tooltip title="Active"><Space size={4}><CheckCircleFilled style={{ color: 'green', fontSize: 16 }} /><Typography.Text style={{ color: 'green' }}>Yes</Typography.Text></Space></Tooltip>
            : <Tooltip title="Inactive"><Space size={4}><CloseCircleFilled style={{ color: 'red', fontSize: 16 }} /><Typography.Text style={{ color: 'red' }}>No</Typography.Text></Space></Tooltip>,
    },
    {
        title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 60,
        render: (_, record) => {
            const items = [
                {
                    key: "view", label: "View", icon: <EyeOutlined />,
                    onClick: (info) => { info?.domEvent?.stopPropagation(); openView(record); },
                },
                {
                    key: "edit", label: "Edit", icon: <EditOutlined />,
                    onClick: (info) => { info?.domEvent?.stopPropagation(); openEdit(record); },
                },
                {
                    key: "toggle",
                    label: record.cus_is_active == 1 ? "Deactivate" : "Activate",
                    icon: record.cus_is_active == 1 ? <CloseCircleFilled /> : <CheckCircleFilled />,
                    onClick: (info) => { info?.domEvent?.stopPropagation(); handleToggle(record, record.cus_is_active != 1); },
                },
                {
                    key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true,
                    onClick: (info) => {
                        info?.domEvent?.stopPropagation();
                        Modal.confirm({
                            title: "Delete Customer",
                            content: "Are you sure you want to delete this customer?",
                            okText: "Yes, Delete",
                            okType: "danger",
                            cancelText: "No",
                            onOk: () => handleDelete(record),
                        });
                    },
                },
            ];
            return (
                <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                    <Button type="text" className="fluid-dots-btn" icon={<MoreOutlined />} />
                </Dropdown>
            );
        },
    },
    ];

    // ── Customer Form Fields (shared between Add & Edit) ──
    // 3-column layout: [Name | Type | Area] [Reg No | Status | Contact] [Email | Outstanding | Ref]
    const renderCustomerFields = (isEdit = false) => (
        <>
            <Row gutter={16}>
                <Col xs={24} sm={8}>
                    <Form.Item
                        label="Customer Name"
                        name="cus_name"
                        rules={[{ required: true, message: 'Customer name is required' }, { max: 200, message: 'Max 200 characters' }]}
                    >
                        <Input placeholder="e.g. John Doe / ABC Corp" />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                    <Form.Item label="Customer Type" name="cus_type">
                        <Select placeholder="Select type" allowClear options={CUSTOMER_TYPE_OPTIONS} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                    <Form.Item label="Customer Area" name="cus_area_id">
                        <Select placeholder="Select area" allowClear showSearch optionFilterProp="children"
                            options={(() => {
                                const base = areas.map(a => ({ value: a.area_id, label: a.area_name }));
                                if (isEdit && currentItem?.cus_area_id && currentItem?.area_name) {
                                    const id = Number(currentItem.cus_area_id);
                                    if (!base.some(o => o.value === id)) {
                                        base.unshift({ value: id, label: currentItem.area_name });
                                    }
                                }
                                return base;
                            })()} />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} sm={8}>
                    <Form.Item label="Registration No" name="cus_reg_no" rules={[{ max: 100, message: 'Max 100 characters' }]}>
                        <Input placeholder="e.g. PV00123456" />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                    <Form.Item label="Status" name="cus_status">
                        <Select placeholder="Select status" allowClear options={STATUS_OPTIONS} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                    <Form.Item label="Contact" name="cus_contact" rules={[{ max: 50, message: 'Max 50 characters' }]}>
                        <Input placeholder="e.g. 0771234567" />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} sm={8}>
                    <Form.Item label="Email" name="cus_email" rules={[{ max: 150, message: 'Max 150 characters' }, { type: 'email', message: 'Invalid email' }]}>
                        <Input placeholder="e.g. customer@example.com" />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                    <Form.Item label="Outstanding Balance" name="cus_outstanding">
                        <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} placeholder="0.00" />
                    </Form.Item>
                </Col>
                {isEdit && (
                    <Col xs={24} sm={8}>
                        <Form.Item label="Reference" name="cus_ref">
                            <Input disabled />
                        </Form.Item>
                    </Col>
                )}
            </Row>
            <Typography.Text strong style={{ display: 'block', marginBottom: 8, color: '#595959' }}>Billing Address</Typography.Text>
            <Row gutter={16}>
                <Col xs={24} sm={16}>
                    <Form.Item label="Address" name="cus_bill_address">
                        <Input placeholder="Street address" />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                    <Form.Item label="Country" name="cus_bill_country">
                        <Select
                            placeholder="Select country"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option.label || '').toLowerCase().includes(input.toLowerCase()) ||
                                (option.value || '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={countries.map(c => ({ value: c.country_code, label: `${c.country_name} (${c.country_code})` }))}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Typography.Text strong style={{ display: 'block', marginBottom: 8, color: '#595959' }}>Shipping Address</Typography.Text>
            <Row gutter={16}>
                <Col xs={24} sm={16}>
                    <Form.Item label="Address" name="cus_ship_address">
                        <Input placeholder="Street address" />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                    <Form.Item label="Country" name="cus_ship_country">
                        <Select
                            placeholder="Select country"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option.label || '').toLowerCase().includes(input.toLowerCase()) ||
                                (option.value || '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={countries.map(c => ({ value: c.country_code, label: `${c.country_name} (${c.country_code})` }))}
                        />
                    </Form.Item>
                </Col>
            </Row>
        </>
    );

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Customers' },
                                { title: 'Customer List' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <TeamOutlined /> Customer List
                        </h1>
                        <p className="fluid-page-subtitle">Manage your customer records</p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setIsAddModalVisible(true)}
                                className="hero-action-btn"
                            >
                                Add New Customer
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} md={6} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Type to search"
                                    prefix={<SearchOutlined />}
                                    allowClear={{ onClear: () => { setSearch(''); } }}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Area:</div>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="All Areas"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    value={filterArea}
                                    onChange={(val) => setFilterArea(val)}
                                    options={areas.map(a => ({ value: a.area_id, label: a.area_name }))}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Status:</div>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="All Statuses"
                                    allowClear
                                    value={filterStatus || undefined}
                                    onChange={(val) => setFilterStatus(val || '')}
                                    options={STATUS_OPTIONS}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={customers}
                            rowKey="cus_id"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 1400 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            {/* Add Modal */}
            <Modal
                title="Add New Customer"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={960}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    {renderCustomerFields(false)}
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Customer</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Customer"
                open={isEditModalVisible}
                onCancel={() => { setIsEditModalVisible(false); editForm.resetFields(); }}
                footer={null}
                width={960}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    {renderCustomerFields(true)}
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => setIsEditModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* View Modal */}
            <Modal
                title={`Customer Details${viewItem ? ` — ${viewItem.cus_name}` : ""}`}
                open={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsViewModalVisible(false)}>Close</Button>
                ]}
                width={680}
            >
                {viewItem && (
                    <>
                        <Descriptions column={2} size="small" bordered>
                            <Descriptions.Item label="ID"><Tag color="blue">#{viewItem.cus_id}</Tag></Descriptions.Item>
                            <Descriptions.Item label="Reference">{viewItem.cus_ref || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Name" span={2}><Typography.Text strong>{viewItem.cus_name}</Typography.Text></Descriptions.Item>
                            <Descriptions.Item label="Type">{viewItem.cus_type || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Reg No">{viewItem.cus_reg_no || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Area">{viewItem.area_name || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Contact">{viewItem.cus_contact || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Email" span={2}>{viewItem.cus_email || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Outstanding">
                                <Typography.Text strong>{viewItem.cus_outstanding ? Number(viewItem.cus_outstanding).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '0.00'}</Typography.Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                {(() => {
                                    if (!viewItem.cus_status) return '—';
                                    const colorMap = { Active: 'green', Inactive: 'default', Suspended: 'red', Pending: 'orange' };
                                    return <Tag color={colorMap[viewItem.cus_status] || 'default'}>{viewItem.cus_status}</Tag>;
                                })()}
                            </Descriptions.Item>
                        </Descriptions>
                        <Typography.Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8, color: '#595959' }}>Billing Address</Typography.Text>
                        <Descriptions column={2} size="small" bordered>
                            <Descriptions.Item label="Address">{viewItem.cus_bill_address || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Country">{viewItem.bill_country_name || '—'}</Descriptions.Item>
                        </Descriptions>
                        <Typography.Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8, color: '#595959' }}>Shipping Address</Typography.Text>
                        <Descriptions column={2} size="small" bordered>
                            <Descriptions.Item label="Address">{viewItem.cus_ship_address || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Country">{viewItem.ship_country_name || '—'}</Descriptions.Item>
                        </Descriptions>
                    </>
                )}
            </Modal>
        </Layout>
    );
};

export default ManageCustomer;
